"use client";
import Icon from "@components/Icon";
import MainDropDownContext from "@contexts/MainDropDownContext";
import {
	closeIconSizes,
	colours,
	device,
	laptopMediaQuery,
	tabletMediaQuery,
} from "@helpers";
import useDeviceDetect from "@helpers/deviceDetect";
import { mixpanelTrack } from "@helpers/mixpanel.helper";
import localImageLoader from "@utils/localImageLoader";
import Image from "next/image";
import { usePathname } from "next/navigation";
import type React from "react";
import { type MouseEvent, useContext, useEffect, useState } from "react";
import styled from "styled-components";
import Expandable from "./Expandable";

type MainDropDownProps = {
	showMainDropdown: boolean;
	setShowMainDropdown: React.Dispatch<React.SetStateAction<boolean>>;
	notificationBarToggle: string;
};
type SpanProps = {
	showMainDropdown: boolean;
	mobileAndTablet: boolean;
};
type DropDownProps = {
	showMainDropdown: boolean;
	positionWhenNavigationBarIsOn: string;
	mobileAndTablet: boolean;
};

type InfoType = {
	title: string;
	content: string;
	iconRef: string;
};

const Span = styled.span<SpanProps>`
  display: ${({ showMainDropdown, mobileAndTablet }) =>
		showMainDropdown && mobileAndTablet ? "visible" : "none"};
  position: fixed;
  right: 30px;
  top: 30px;
  z-index: 103;
`;

const DropDown = styled.div<DropDownProps>`
  background-color: ${colours.WHITE};
  display: ${({ showMainDropdown }) => (showMainDropdown ? "visible" : "none")};
  height: fit-content;
  position: absolute;
  ${({ positionWhenNavigationBarIsOn }) =>
		positionWhenNavigationBarIsOn === "On" ? "top: 117px" : "top: 70px"};
  top: 117px;
  width: 100%;
  z-index: 102;
  @media (max-width: ${device.tabletL}) {
    top: 110px;
  }
  ${({ mobileAndTablet }) =>
		mobileAndTablet &&
		`
    background-color: ${colours.PEACH};
    height: 100vh;
    left: 0px;
    position: fixed;
    margin-top: 0;
    margin-bottom: 0;
    max-width: 100%;
    overflow-y: scroll;
    top: 0px !important;
    padding: 0 !important;
    & > div {
      align-items: center !important;flex-direction: column !important;margin-top: 10vh !important;padding: 0 20px !important;
    }
  `}
`;

const Div = styled.div<{ mobileAndTablet: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  padding: 20px 10vw 40px;
  flex-wrap: wrap;

  @media (max-width: ${device.tabletL}) {
    flex-direction: column;
    align-items: center;
  }
  @media (max-width: ${device.laptopM}) {
    padding: 20px 4vw 40px;
  }
`;

const MarketingMessage = styled.section<{ mobileAndTablet: boolean }>`
  margin-top: 20px;
  max-width: 600px;
  word-wrap: break-word;
  ${({ mobileAndTablet }) =>
		mobileAndTablet &&
		`
    width: fit-content;
  `}
`;

const AppDownload = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
  & > * {
    margin: 20px;
  }
  margin: 20px 0 0 0;
  & > * {
    margin: 20px;
  }
  @media (max-width: ${device.mobileXL}) {
    flex-direction: column;
  }
`;

const SwitchStep = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex: 1;
  margin-top: 20px;
`;

const IconContainer = styled.div`
  align-items: center;
  align-self: flex-start;
  background-color: ${colours.WHITE};
  border-radius: 50%;
  display: flex;
  height: 38px;
  justify-content: center;
  margin-right: 12px;
  width: 38px;

  ${tabletMediaQuery} {
    height: 48px;
    margin-right: 10px;
    width: 48px;
  }

  ${laptopMediaQuery} {
    height: 48px;
    margin-right: 10px;
    width: 48px;
  }

  @media (min-width: ${device.desktopS}) {
    height: 50px;
    margin-right: 20px;
    width: 50px;
  }
`;

const ExpandableSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const SwitchExpanable = styled(Expandable)<{ mobileAndTablet: boolean }>`
  justify-content: space-between;
  margin-top: 0px;
  min-height: 38px;
  padding: 0px;
  width: 300px;
  & > p {
    max-width: 500px;
  }

  ${tabletMediaQuery} {
    width: 500px;

    min-height: 48px;
  }

  ${laptopMediaQuery} {
    width: 500px;

    min-height: 48px;
  }
  ${({ mobileAndTablet }) =>
		mobileAndTablet &&
		`
  @media (max-width: ${device.tablet}) {
    &:first-child {
      max-height: 44px;
    }
    & > p {
      max-width: 400px;
    }
  }
  @media (max-width: ${device.mobileM}) {
    & > p {
      max-width: 300px;
    }
  }
  `}
  @media (min-width: ${device.desktopS}) {
    width: 500px;

    min-height: 52px;
  }
`;

const SwitchDetail = styled.span`
  font-family: var(--euclid-regular);
  margin-top: 10px;

  ${laptopMediaQuery} {
    max-width: 500px;
  }
`;

const AppContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 8px;
  & > * {
    max-height: 45px !important;
    max-width: 150px !important;
    margin: 0 10px;
  }
  & a {
    max-width: 100%;
	display: flex;
    justify-content: center;
  }
`;

const StyledLink = styled.a`
  align-items: center;
  border-radius: 10px;
  display: flex;
  font-family: var(--euclid-medium);

  padding: 11px 23px;
  box-sizing: border-box;
  font-family: var(--euclid-medium);
  border-radius: 12px;
  background-color: ${colours.WATERMELON};
  color: ${colours.MIDNIGHT_BLUE};
  border-radius: 12px;
  border: 2px solid ${colours.WATERMELON};
  outline: none;
  :hover {
    cursor: pointer;
    border: 2px solid ${colours.WATERMELON} !important;
    background-color: ${colours.WHITE};
    color: ${colours.MIDNIGHT_BLUE} !important;
  }
  :disabled {
    opacity: 0.5;
  }
  :hover:enabled {
    cursor: pointer;
  }
  :active {
    transition: 0.3s all;
    transform: translateY(3px);
  }
`;

const iconSizes = {
	iconMobile: 25,
	iconTablet: 35,
	iconLaptop: 35,
	iconDesktop: 40,
	iconDesktopLarge: 40,
};

const MainDropDown = ({
	showMainDropdown,
	setShowMainDropdown,
	notificationBarToggle,
}: MainDropDownProps) => {
	const context = useContext(MainDropDownContext);

	const pathname = usePathname();

	const componentData = context?.MainDropDownData[0]?.fields;
	const Items = [
		{
			id: 1,
			title: "1. Download the app or continue on the web to sign up",
			iconRef: "mobile",
			content:
				"Choose from one of three plans with great data inclusions. Don’t worry, your plan won’t actually start until you activate your SIM, so you won’t lose any days.",
		},
		{
			id: 2,
			title: "2. Order your SIM with free express delivery",
			iconRef: "mail",
			content:
				"It should arrive within 1-3 days. Alternatively, skip delivery all together with eSIM.",
		},
		{
			id: 3,
			title: "3. Activate your SIM in the app",
			iconRef: "sim-valid",
			content:
				"Pop back into the felix app whenever you're ready to activate your new SIM. You can bring your number over or get a new one!",
		},
	];
	const callToActionFields = componentData?.callToAction.fields;
	const mainDropdownAppLinks = context?.MainDropDownData[1]?.fields;
	const { isAndroid, isIOS, isMobile, isTablet } = useDeviceDetect();
	const [isMobileAndTablet, setIsMobileAndTablet] = useState(false);
	useEffect(() => {
		const mobileAndTablet = isMobile || isTablet;
		setIsMobileAndTablet(mobileAndTablet);
	}, [isMobile, isTablet]);

	const [isIOSPhone, setIsIOSPhone] = useState(false);
	const [isAndroidPhone, setIsAndroidPhone] = useState(false);

	const positionWhenNavigationBarIsOn = notificationBarToggle;
	const handleMouseEvent = (e: MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		setShowMainDropdown(false);
	};
	useEffect(() => {
		setIsIOSPhone(isIOS);
		setIsAndroidPhone(isAndroid);
	}, [isAndroid, isIOS]);

	return (
		<>
			{isMobileAndTablet && (
				<Span
					showMainDropdown={showMainDropdown}
					mobileAndTablet={isMobileAndTablet}
				>
					<Icon
						iconName="close"
						onClick={handleMouseEvent}
						iconSizes={closeIconSizes}
						alt="close"
					/>
				</Span>
			)}
			<DropDown
				positionWhenNavigationBarIsOn={positionWhenNavigationBarIsOn}
				showMainDropdown={showMainDropdown}
				mobileAndTablet={isMobileAndTablet}
			>
				<Div mobileAndTablet={isMobileAndTablet}>
					<MarketingMessage mobileAndTablet={isMobileAndTablet}>
						<h5>Join thousands of other felix users today</h5>
						{Items?.map((info: InfoType, id: number) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
							<SwitchStep key={id + info.title}>
								<IconContainer>
									<Icon
										iconName={info.iconRef}
										iconSizes={iconSizes}
										alt={info.iconRef}
									/>
								</IconContainer>
								<ExpandableSection
									onClick={() =>
										mixpanelTrack("switchnow_navbar_steps_clicked", {
											dropdown_title: info.title,
											dropdown_URL: pathname,
										})
									}
								>
									<SwitchExpanable
										mobileAndTablet={isMobileAndTablet}
										key={info.title}
										renderDetail={() => (
											<SwitchDetail>{info.content}</SwitchDetail>
										)}
										title={info.title}
									/>
								</ExpandableSection>
							</SwitchStep>
						))}
					</MarketingMessage>
					<AppDownload>
						{isMobileAndTablet ? (
							<>
								{isIOSPhone && (
									<a
										href="https://flx.mobi/bQkA1fQX7db"
										target="_blank"
										rel="noreferrer"
										onClick={() =>
											mixpanelTrack("switchnow_navbar_download_app_clicked", {
												CTA_location: "Nav Header",
												CTA_Text: "App Store",
											})
										}
									>
										<AppContainer>
											<Image
												alt="Get it on Appstore"
												src="/images/AppStoreBadge.png"
												width={162.5}
												height={48}
												data-gtm-cta-text="appleUrl"
												data-gtm-cta-position="Nav Header"
											/>
										</AppContainer>
									</a>
								)}
								{isAndroidPhone && (
									<a
										href="https://flx.mobi/FagEfRLX7db"
										target="_blank"
										rel="noreferrer"
										onClick={() =>
											mixpanelTrack("switchnow_navbar_download_app_clicked", {
												CTA_location: "Nav Header",
												CTA_Text: "Google Play",
											})
										}
									>
										<AppContainer>
											<Image
												alt="Get it on Google Play"
												src="/images/PlayStoreBadge.png"
												width={162.5}
												height={48}
												data-gtm-cta-text="androidUrl"
												data-gtm-cta-position="Nav Header"
											/>
										</AppContainer>
									</a>
								)}
							</>
						) : (
							<>
								<h5>{callToActionFields?.mainText}</h5>

								<AppContainer>
									<a
										href="https://flx.mobi/FagEfRLX7db"
										target="_blank"
										rel="noreferrer"
										onClick={() =>
											mixpanelTrack("switchnow_navbar_download_app_clicked", {
												CTA_location: "Nav Header",
												CTA_Text: "Google Play",
											})
										}
									>
										<Image
											loader={localImageLoader}
											alt="Get it on Google Play"
											src="/images/PlayStoreBadge.png"
											width={162.5}
											height={48}
											data-gtm-cta-text="androidUrl"
											data-gtm-cta-position="Nav Header"
										/>
									</a>
									<a
										href="https://flx.mobi/bQkA1fQX7db"
										target="_blank"
										rel="noreferrer"
										onClick={() =>
											mixpanelTrack("switchnow_navbar_download_app_clicked", {
												CTA_location: "Nav Header",
												CTA_Text: "App Store",
											})
										}
									>
										<Image
											loader={localImageLoader}
											alt="Get it on Appstore"
											src="/images/AppStoreBadge.png"
											width={162.5}
											height={48}
											data-gtm-cta-text="appleUrl"
											data-gtm-cta-position="Nav Header"
										/>
									</a>
								</AppContainer>
							</>
						)}
						<StyledLink
							href="/plan"
							style={{ textDecoration: "none" }}
							data-gtm-cta-position="Nav Header"
							data-gtm-cta-text="or sign up on the web"
							onClick={() =>
								mixpanelTrack("switchnow_navbar_signup_clicked", {
									cta_location: "Nav Header",
								})
							}
						>
							Choose your plan
						</StyledLink>
					</AppDownload>
				</Div>
			</DropDown>
		</>
	);
};

export default MainDropDown;
